import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Facebook, Save, Copy, RefreshCw, Info, Link2, CheckCircle2, Instagram } from "lucide-react";

const empty = { app_id: "", app_secret: "", page_id: "", page_access_token: "", ig_user_id: "", graph_version: "v26.0" };

export default function MetaSettings() {
  const { user } = useAuth();
  const [form, setForm] = useState(empty);
  const [secretSet, setSecretSet] = useState(false);
  const [tokenSet, setTokenSet] = useState(false);
  const [verifyToken, setVerifyToken] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/meta/webhook`;

  useEffect(() => {
    api.get("/settings/meta").then((r) => {
      const d = r.data || {};
      setForm({
        app_id: d.app_id || "", app_secret: "", page_id: d.page_id || "",
        page_access_token: "", ig_user_id: d.ig_user_id || "",
        graph_version: d.graph_version || "v26.0",
      });
      setSecretSet(!!d.app_secret_set);
      setTokenSet(!!d.page_access_token_set);
      setVerifyToken(d.verify_token || "");
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copied`));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/settings/meta", form);
      setSecretSet(!!data.app_secret_set);
      setTokenSet(!!data.page_access_token_set);
      setVerifyToken(data.verify_token || "");
      setForm((f) => ({ ...f, app_secret: "", page_access_token: "" }));
      toast.success("Meta settings saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const regenerate = async () => {
    try {
      const { data } = await api.post("/settings/meta/regenerate-token");
      setVerifyToken(data.verify_token);
      toast.success("New verify token generated — update it in the Meta Developer console");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const subscribe = async () => {
    setSubscribing(true);
    try {
      await api.post("/meta/subscribe-leads");
      toast.success("Page subscribed to Lead Ads (leadgen) events");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSubscribing(false); }
  };

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="meta-settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight flex items-center gap-2">
          <Facebook size={26} className="text-blue-600" /> Meta Integration
        </h1>
        <p className="text-sm text-slate-500 mt-1">Connect Facebook &amp; Instagram — capture Lead Ads automatically and publish posts from Marketing.</p>
      </div>

      <form onSubmit={save} className="p-6 rounded-xl bg-white border border-slate-200 shadow-sm space-y-5">
        <div className="flex items-center gap-3 pb-2">
          <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center"><Facebook size={20} /></div>
          <h3 className="text-base font-semibold text-slate-800 font-heading">Meta App Credentials</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>App ID</label><input data-testid="meta-app-id" value={form.app_id} onChange={(e) => set("app_id", e.target.value)} className={inputCls} placeholder="1234567890123456" /></div>
          <div><label className={labelCls}>App Secret {secretSet && <span className="text-emerald-600 normal-case">· saved</span>}</label><input data-testid="meta-app-secret" type="password" value={form.app_secret} onChange={(e) => set("app_secret", e.target.value)} className={inputCls} placeholder={secretSet ? "•••••••• (leave blank to keep)" : "App secret"} /></div>
          <div><label className={labelCls}>Page ID</label><input data-testid="meta-page-id" value={form.page_id} onChange={(e) => set("page_id", e.target.value)} className={inputCls} placeholder="Facebook Page ID" /></div>
          <div><label className={labelCls}>Page Access Token {tokenSet && <span className="text-emerald-600 normal-case">· saved</span>}</label><input data-testid="meta-page-token" type="password" value={form.page_access_token} onChange={(e) => set("page_access_token", e.target.value)} className={inputCls} placeholder={tokenSet ? "•••••••• (leave blank to keep)" : "Long-lived page token"} /></div>
          <div><label className={labelCls}><span className="inline-flex items-center gap-1"><Instagram size={13} /> Instagram Business User ID</span></label><input data-testid="meta-ig-user-id" value={form.ig_user_id} onChange={(e) => set("ig_user_id", e.target.value)} className={inputCls} placeholder="1784xxxxxxxxxx (optional)" /></div>
          <div><label className={labelCls}>Graph API Version</label><input data-testid="meta-graph-version" value={form.graph_version} onChange={(e) => set("graph_version", e.target.value)} className={inputCls} placeholder="v26.0" /></div>
        </div>
        <div className="flex justify-end">
          <button data-testid="save-meta-settings-button" type="submit" className={btnPrimary}><Save size={16} /> Save Settings</button>
        </div>
      </form>

      <div className="p-6 rounded-xl bg-white border border-slate-200 shadow-sm space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center"><Link2 size={20} /></div>
          <h3 className="text-base font-semibold text-slate-800 font-heading">Webhook (Lead Ads)</h3>
        </div>
        <p className="text-xs text-slate-500">Paste these two values in your Meta App → Webhooks → Page → <strong>leadgen</strong> subscription.</p>
        <div>
          <label className={labelCls}>Callback URL</label>
          <div className="flex gap-2">
            <input data-testid="meta-webhook-url" readOnly value={webhookUrl} className={`${inputCls} bg-slate-50 font-mono text-xs`} />
            <button type="button" data-testid="copy-webhook-url" onClick={() => copy(webhookUrl, "Callback URL")} className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm flex items-center gap-1.5 whitespace-nowrap"><Copy size={14} /> Copy</button>
          </div>
        </div>
        <div>
          <label className={labelCls}>Verify Token</label>
          <div className="flex gap-2">
            <input data-testid="meta-verify-token" readOnly value={verifyToken} className={`${inputCls} bg-slate-50 font-mono text-xs`} />
            <button type="button" data-testid="copy-verify-token" onClick={() => copy(verifyToken, "Verify token")} className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm flex items-center gap-1.5 whitespace-nowrap"><Copy size={14} /> Copy</button>
            <button type="button" data-testid="regenerate-verify-token" onClick={regenerate} className="px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm flex items-center gap-1.5 whitespace-nowrap"><RefreshCw size={14} /> Regenerate</button>
          </div>
        </div>
        <div className="pt-2 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-slate-500">After saving credentials, subscribe your Page so Facebook starts sending lead events.</div>
          <button type="button" data-testid="subscribe-lead-ads-button" onClick={subscribe} disabled={subscribing} className={btnPrimary}>
            <CheckCircle2 size={16} /> {subscribing ? "Subscribing…" : "Subscribe Page to Lead Ads"}
          </button>
        </div>
      </div>

      <div className="p-6 rounded-xl bg-white border border-slate-200 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center"><Info size={20} /></div>
          <h3 className="text-base font-semibold text-slate-800 font-heading">Setup Guide</h3>
        </div>
        <ol className="space-y-3 text-sm text-slate-600 list-decimal list-inside">
          <li>Go to <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Meta for Developers</a> → create an App (type: Business). Copy the <strong>App ID</strong> and <strong>App Secret</strong> from Settings → Basic.</li>
          <li>Add products to the app: <strong>Webhooks</strong>, <strong>Facebook Login for Business</strong>, and <strong>Marketing API</strong> (for Lead Ads).</li>
          <li>Generate a token in <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Graph API Explorer</a> with permissions: <code className="text-xs bg-slate-100 px-1 rounded">leads_retrieval</code>, <code className="text-xs bg-slate-100 px-1 rounded">pages_manage_metadata</code>, <code className="text-xs bg-slate-100 px-1 rounded">pages_manage_posts</code>, <code className="text-xs bg-slate-100 px-1 rounded">pages_read_engagement</code>, <code className="text-xs bg-slate-100 px-1 rounded">instagram_basic</code>, <code className="text-xs bg-slate-100 px-1 rounded">instagram_content_publish</code>. Then call <code className="text-xs bg-slate-100 px-1 rounded">GET /me/accounts</code> to get your <strong>Page ID</strong> and long-lived <strong>Page Access Token</strong>.</li>
          <li>For Instagram publishing, convert the IG account to a Business/Professional account linked to the Page, then call <code className="text-xs bg-slate-100 px-1 rounded">GET /{"{page-id}"}?fields=instagram_business_account</code> to get the <strong>IG User ID</strong>.</li>
          <li>Save all values above, then in the app dashboard: Webhooks → Page → subscribe to the <strong>leadgen</strong> field using the Callback URL and Verify Token shown here.</li>
          <li>Click <strong>Subscribe Page to Lead Ads</strong> so Facebook delivers new leads to this platform automatically — they appear in Leads with source "Facebook Lead Ad".</li>
          <li>Publish posts anytime from the <strong>Marketing → Social Post</strong> tab.</li>
        </ol>
      </div>
    </div>
  );
}
